import { Box, Text, Flex } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  AssignmentSummaryAccordion,
  AssignmentSummaryAccordionRow,
} from './SharedComponents';
import type { PortfolioTestStats } from './TestStats';
import { getNumberedSubRowLabel } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';

export const EligibilityStats: React.FC<{
  initialStats: PortfolioTestStats;
  proposedStats: PortfolioTestStats;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}> = ({ initialStats, proposedStats, creditAgreement }) => {
  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: { loanProductMechanics },
    },
  } = creditAgreement;

  return (
    <Box mt="24px">
      <Flex
        borderBottom="1px solid"
        borderColor={colors.gray[200]}
        p="8px 0px 8px"
        mb="16px"
      >
        <Text fontWeight="bold">Eligibility</Text>
      </Flex>
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <AssignmentSummaryAccordionRow
            label="Total Eligibility Kickout"
            calcTerminologiesLabel="Total Eligibility Kickout"
            initialValue={initialStats.totalEligibilityKickout}
            proposedValue={proposedStats.totalEligibilityKickout}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            fullRowBackground
            background="medium"
          />
        )}
        accordionPanel={
          <Box>
            {_.map(loanProductMechanics, (loanProduct) => {
              let eligibilityTestIndex = -1;
              return (
                <Box
                  mt="8px"
                  key={`eligibility-kickout-${loanProduct.entity.id}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={loanProduct.entity.name}
                        initialValue={
                          initialStats.eligibilityKickoutByLoanProductId[
                            loanProduct.entity.id
                          ]
                        }
                        proposedValue={
                          proposedStats.eligibilityKickoutByLoanProductId[
                            loanProduct.entity.id
                          ]
                        }
                        valueType="currency"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        isSecondLevelAccordion
                        fullRowBackground
                        background="light"
                      />
                    )}
                    accordionPanel={
                      <Box mt="16px">
                        {_.map(
                          loanProduct.eligibilityTests,
                          (eligibilityTest) => {
                            if (
                              eligibilityTest._config_.eligibilityTestConfig
                            ) {
                              eligibilityTestIndex += 1;
                              const eligibilityTestConfig =
                                eligibilityTest._config_.eligibilityTestConfig;
                              const initialEligibilityReduction =
                                initialStats.eligibilityTestsByLoanProductId[
                                  loanProduct.entity.id
                                ][eligibilityTestConfig.propertyKey];
                              const proposedEligibilityReduction =
                                proposedStats.eligibilityTestsByLoanProductId[
                                  loanProduct.entity.id
                                ][eligibilityTestConfig.propertyKey];
                              return (
                                <Box
                                  mt="16px"
                                  key={`loan-product-eligibility-test-${eligibilityTestConfig.propertyKey}`}
                                >
                                  <AssignmentSummaryAccordionRow
                                    subLabel={getNumberedSubRowLabel(
                                      eligibilityTestIndex,
                                      eligibilityTestConfig.propertyKey,
                                    )}
                                    initialValue={initialEligibilityReduction}
                                    proposedValue={proposedEligibilityReduction}
                                    valueType="currency"
                                    isSecondLevelAccordion
                                    background="light"
                                  />
                                </Box>
                              );
                            }
                            return (
                              <Box
                                key={`loan-product-eligibility-test-${eligibilityTest.value}`}
                              />
                            );
                          },
                        )}
                      </Box>
                    }
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
      <Box mt="8px">
        <AssignmentSummaryAccordion
          accordionButton={({ hasAccordionPanel, isExpanded }) => (
            <AssignmentSummaryAccordionRow
              label="Total Eligible Exposure"
              calcTerminologiesLabel="Total Eligible Exposure"
              initialValue={initialStats.totalExposureLessEligibilityKickouts}
              proposedValue={proposedStats.totalExposureLessEligibilityKickouts}
              valueType="currency"
              hasAccordionPanel={hasAccordionPanel}
              isExpanded={isExpanded}
              fullRowBackground
              background="medium"
            />
          )}
          accordionPanel={
            <Box>
              {_.map(loanProductMechanics, (loanProduct) => {
                return (
                  <Box
                    mt="8px"
                    key={`loan-product-eligible-exposure-${loanProduct.entity.id}`}
                  >
                    <AssignmentSummaryAccordionRow
                      subLabel={loanProduct.entity.name}
                      initialValue={
                        initialStats
                          .totalExposureLessEligibilityKickoutsByLoanProductId[
                          loanProduct.entity.id
                        ]
                      }
                      proposedValue={
                        proposedStats
                          .totalExposureLessEligibilityKickoutsByLoanProductId[
                          loanProduct.entity.id
                        ]
                      }
                      valueType="currency"
                      fullRowBackground
                      background="light"
                    />
                  </Box>
                );
              })}
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
